/**
 * 標準暗号の翻訳と 6 フレーム ORF（§5.6 I73–I77）。
 */

import { CODE_A, CODE_G, CODE_T, reverseComplementCodes } from '../sequence/nucleotideCodes';
import type { Topology } from '../sequence/interval';
import { PREDICTED_ORF_NOTE, type GenomicFeatureV1, type Strand } from '../annotation/types';

const CODON_AA: Record<string, string> = {
  TTT: 'F', TTC: 'F', TTA: 'L', TTG: 'L',
  TCT: 'S', TCC: 'S', TCA: 'S', TCG: 'S',
  TAT: 'Y', TAC: 'Y', TAA: '*', TAG: '*',
  TGT: 'C', TGC: 'C', TGA: '*', TGG: 'W',
  CTT: 'L', CTC: 'L', CTA: 'L', CTG: 'L',
  CCT: 'P', CCC: 'P', CCA: 'P', CCG: 'P',
  CAT: 'H', CAC: 'H', CAA: 'Q', CAG: 'Q',
  CGT: 'R', CGC: 'R', CGA: 'R', CGG: 'R',
  ATT: 'I', ATC: 'I', ATA: 'I', ATG: 'M',
  ACT: 'T', ACC: 'T', ACA: 'T', ACG: 'T',
  AAT: 'N', AAC: 'N', AAA: 'K', AAG: 'K',
  AGT: 'S', AGC: 'S', AGA: 'R', AGG: 'R',
  GTT: 'V', GTC: 'V', GTA: 'V', GTG: 'V',
  GCT: 'A', GCC: 'A', GCA: 'A', GCG: 'A',
  GAT: 'D', GAC: 'D', GAA: 'E', GAG: 'E',
  GGT: 'G', GGC: 'G', GGA: 'G', GGG: 'G',
};

const CODE_TO_BASE = ['A', 'C', 'G', 'T', 'N'] as const;

export function translateCodonCodes(a: number, b: number, c: number): string {
  const codon = `${CODE_TO_BASE[a] ?? 'N'}${CODE_TO_BASE[b] ?? 'N'}${CODE_TO_BASE[c] ?? 'N'}`;
  return CODON_AA[codon] ?? 'X';
}

export function translateCodes(codes: Uint8Array, frame = 0): string {
  let out = '';
  for (let i = frame; i + 2 < codes.length; i += 3) {
    out += translateCodonCodes(codes[i], codes[i + 1], codes[i + 2]);
  }
  return out;
}

export interface PredictedOrf {
  start: number;
  end: number;
  strand: Strand;
  aaLength: number;
  translation: string;
}

export function minOrfAaLength(scanBases: number): number {
  if (scanBases <= 200) return 10;
  if (scanBases <= 2000) return 20;
  if (scanBases <= 20_000) return 30;
  return 50;
}

export function findOrfs(
  codes: Uint8Array,
  options: { genomeLength: number; topology: Topology; minAa?: number }
): PredictedOrf[] {
  const minAa = options.minAa ?? minOrfAaLength(options.genomeLength);
  const plus = findOrfsOnStrand(codes, 1, minAa, options.genomeLength, options.topology);
  const minusCodes = reverseComplementCodes(codes);
  const minus = findOrfsOnStrand(minusCodes, -1, minAa, options.genomeLength, options.topology).map((orf) => ({
    ...orf,
    start: options.genomeLength - orf.end,
    end: options.genomeLength - orf.start,
  }));
  return [...plus, ...minus].sort((a, b) => a.start - b.start || b.aaLength - a.aaLength);
}

function findOrfsOnStrand(
  codes: Uint8Array,
  strand: Strand,
  minAa: number,
  genomeLength: number,
  topology: Topology
): PredictedOrf[] {
  const found: PredictedOrf[] = [];
  const n = codes.length;
  const extend = topology === 'circular' ? n : 0;

  for (let frame = 0; frame < 3; frame += 1) {
    for (let i = frame; i + 2 < n + extend; i += 3) {
      const ia = i % n;
      if (codes[ia] !== CODE_A || codes[(i + 1) % n] !== CODE_T || codes[(i + 2) % n] !== CODE_G) {
        continue;
      }
      let aa = 'M';
      let j = i + 3;
      const limit = topology === 'circular' ? i + n : n;
      while (j + 2 < limit) {
        const aaCode = translateCodonCodes(codes[j % n], codes[(j + 1) % n], codes[(j + 2) % n]);
        if (aaCode === '*') {
          if (aa.length >= minAa) {
            const rawStart = i % n;
            const stopEnd = (j + 3) % n;
            const end = stopEnd === 0 ? n : stopEnd <= rawStart ? stopEnd + genomeLength : stopEnd;
            found.push({ start: rawStart, end, strand, aaLength: aa.length, translation: aa });
          }
          break;
        }
        aa += aaCode;
        j += 3;
      }
    }
  }
  return found;
}

export function predictedOrfsToFeatures(
  orfs: PredictedOrf[],
  existing: GenomicFeatureV1[]
): GenomicFeatureV1[] {
  const occupied = new Set(
    existing
      .filter((feature) => feature.type === 'CDS' || feature.type === 'gene')
      .map((feature) => `${feature.start}:${feature.end}:${feature.strand}`)
  );
  return orfs
    .filter((orf) => !occupied.has(`${orf.start}:${orf.end}:${orf.strand}`))
    .map((orf, index) => ({
      id: `orf-${index + 1}`,
      type: 'CDS',
      name: `ORF${index + 1} (${orf.aaLength} aa)`,
      start: orf.start,
      end: orf.end,
      strand: orf.strand,
      note: PREDICTED_ORF_NOTE,
      qualifiers: { translation: orf.translation.slice(0, 80) },
    }));
}
